// Small UI kit: toasts, badges, table, modal, generic form
import { Fragment, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import i18n, { LANGUAGES, setLanguage } from '../i18n';
import { api, ApiError } from '../api';
import { fromCents } from '../format';
import { AddressInput, LookupHint } from './smart';

// ---------- Toasts ----------
type ToastMsg = { text: string; error?: boolean; id: number };
let push: ((m: ToastMsg) => void) | null = null;
export const toast = (text: string, error = false) => push?.({ text, error, id: Date.now() });
export function errorText(e: unknown) {
  if (e instanceof ApiError) {
    const base = i18n.t('err.' + e.code, { defaultValue: i18n.t('err.generic') });
    const avail = (e.details as { available?: number } | undefined)?.available;
    return e.code === 'insufficient_stock' && avail != null ? `${base} (${avail})` : base;
  }
  return i18n.t('err.generic');
}
export const toastError = (e: unknown) => toast(errorText(e), true);

export function Toaster() {
  const [m, setM] = useState<ToastMsg | null>(null);
  useEffect(() => {
    push = setM;
    return () => { push = null; };
  }, []);
  useEffect(() => {
    if (!m) return;
    const t = setTimeout(() => setM(null), 2800);
    return () => clearTimeout(t);
  }, [m]);
  return <div id="toast" role="status" aria-live="polite" className={m ? 'show' + (m.error ? ' err' : '') : ''}>{m?.text}</div>;
}

// ---------- Mutations with toast + cache invalidation ----------
export function useAction<TVars = void, TRes = unknown>(fn: (v: TVars) => Promise<TRes>, opts: { invalidate?: QueryKey[]; success?: string; onSuccess?: (r: TRes, v: TVars) => void } = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: async (r, v) => {
      await Promise.all((opts.invalidate ?? []).map(k => qc.invalidateQueries({ queryKey: k })));
      if (opts.success) toast(opts.success);
      opts.onSuccess?.(r, v);
    },
    onError: toastError
  });
}

// ---------- Badges ----------
export const Badge = ({ kind, children, title }: { kind: string; children: ReactNode; title?: string }) =>
  <span className={`badge b-${kind}`} title={title}>{children}</span>;
export function StatusTag({ status }: { status: string }) {
  const { t } = useTranslation();
  return <span className={`st st-${status}`}>{t('status.' + status)}</span>;
}
export function PmBadge({ status }: { status?: string | null }) {
  const { t } = useTranslation();
  return status ? <Badge kind={status}>{t('pm.' + status)}</Badge> : <span className="sub">—</span>;
}
export function LevelBadge({ level, invert = false }: { level: string; invert?: boolean }) {
  const { t } = useTranslation();
  const k = invert ? (level === 'high' ? 'overdue' : level === 'medium' ? 'due_soon' : 'normal') : (level === 'high' ? 'ok' : level === 'medium' ? 'due_soon' : 'overdue');
  return <Badge kind={k}>{t('ai.level.' + level)}</Badge>;
}

// ---------- Layout bits ----------
export const PageHead = ({ title, sub, children, back }: { title: ReactNode; sub?: ReactNode; children?: ReactNode; back?: ReactNode }) => (
  <div className="head">{back}<div><h1>{title}</h1>{sub && <div className="sub">{sub}</div>}</div><div className="grow" />{children}</div>
);
export const Card = ({ title, actions, children, className = '', style }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string; style?: React.CSSProperties }) => (
  <div className={'card ' + className} style={style}>
    {(title || actions) && <div className="head" style={{ marginBottom: 8 }}>{title && <h2 style={{ margin: 0 }}>{title}</h2>}<div className="grow" />{actions}</div>}
    {children}
  </div>
);
export function Loading() {
  const { t } = useTranslation();
  return <div className="sub">{t('common.loading')}</div>;
}
export function ErrorBox({ error }: { error: unknown }) {
  return <div className="card empty">{errorText(error)}</div>;
}

// ---------- Table ----------
export interface Column<T> { label: string; render: (r: T, i: number) => ReactNode; num?: boolean; key?: string }
export function Table<T>({ columns, rows, onRow, empty, foot }: { columns: Column<T>[]; rows: T[]; onRow?: (r: T) => void; empty?: string; foot?: ReactNode }) {
  const { t } = useTranslation();
  if (!rows.length) return <div className="empty">{empty ?? t('common.none')}</div>;
  return (
    <div className="tbl-wrap"><table>
      <thead><tr>{columns.map((c, i) => <th key={i} className={c.num ? 'num' : ''}>{c.label}</th>)}</tr></thead>
      <tbody>{rows.map((r, i) => (
        <tr key={i} className={onRow ? 'click' : ''} onClick={e => {
          if (!onRow || (e.target as HTMLElement).closest('button, a, input, select')) return;
          onRow(r);
        }}>{columns.map((c, j) => <td key={j} className={c.num ? 'num' : ''}>{c.render(r, i)}</td>)}</tr>
      ))}</tbody>
      {foot}
    </table></div>
  );
}

// ---------- Modal ----------
export function Modal({ title, onClose, children, wide }: { title: ReactNode; onClose: () => void; children: ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    setTimeout(() => ref.current?.querySelector<HTMLElement>('input:not([type=hidden]), select, textarea')?.focus(), 30);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="overlay" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" ref={ref} role="dialog" aria-modal="true" style={wide ? { width: 'min(900px,100%)' } : undefined}>
        <h2>{title}</h2>{children}
      </div>
    </div>
  );
}

// ---------- Form ----------
export type FieldType = 'text' | 'email' | 'password' | 'number' | 'money' | 'date' | 'textarea' | 'select' | 'checkbox';
export interface Field {
  name: string; label: string; type?: FieldType; required?: boolean; full?: boolean;
  options?: [string | number, string][]; blank?: string | false; step?: string; placeholder?: string;
  mono?: boolean; upper?: boolean; maxLength?: number; disabled?: boolean;
  /** Smart field: VIN decode, plate match, USDOT/MC carrier lookup or address autocomplete */
  lookup?: 'vin' | 'plate' | 'carrier' | 'address';
}
type Values = Record<string, unknown>;

/** Converts form strings into API values: numbers, cents for money fields, null for empty */
export function readValues(fields: Field[], raw: Values): Values {
  const out: Values = {};
  for (const f of fields) {
    const v = raw[f.name];
    if (f.type === 'checkbox') { out[f.name] = !!v; continue; }
    if (v === '' || v === undefined || v === null) {
      // Empty numbers are omitted (server default applies); empty text/select becomes null (clears the value)
      if (f.type !== 'number' && f.type !== 'money' && f.type !== 'password') out[f.name] = f.required ? '' : null;
      continue;
    }
    if (f.type === 'number') out[f.name] = Number(v);
    else if (f.type === 'money') out[f.name] = Math.round(Number(v) * 100);
    else if (f.type === 'select' && f.options?.length && typeof f.options[0]![0] === 'number') out[f.name] = Number(v);
    else out[f.name] = f.upper ? String(v).trim().toUpperCase() : String(v).trim();
  }
  return out;
}

export function initialValues(fields: Field[], values: object = {}): Values {
  const out: Values = {};
  for (const f of fields) {
    const v = (values as Values)[f.name];
    out[f.name] = f.type === 'checkbox' ? !!v : f.type === 'money' ? fromCents(v as number) : v ?? '';
  }
  return out;
}

export function FieldInput({ f, value, onChange }: { f: Field; value: unknown; onChange: (v: unknown) => void }) {
  const style = { ...(f.mono ? { fontFamily: 'var(--mono)' } : {}), ...(f.upper ? { textTransform: 'uppercase' as const } : {}) };
  if (f.type === 'checkbox') {
    return <label className={'f inline ' + (f.full ? 'full' : '')}><input type="checkbox" checked={!!value} disabled={f.disabled} onChange={e => onChange(e.target.checked)} /> {f.label}</label>;
  }
  let input: ReactNode;
  if (f.type === 'select') {
    input = (
      <select value={String(value ?? '')} required={f.required} disabled={f.disabled} onChange={e => onChange(e.target.value)}>
        {f.blank !== false && <option value="">{f.blank || '—'}</option>}
        {f.options?.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    );
  } else if (f.lookup === 'address') {
    input = <AddressInput value={String(value ?? '')} required={f.required} placeholder={f.placeholder} disabled={f.disabled} onChange={onChange} />;
  } else if (f.type === 'textarea') {
    input = <textarea value={String(value ?? '')} required={f.required} placeholder={f.placeholder} disabled={f.disabled} onChange={e => onChange(e.target.value)} />;
  } else {
    const type = f.type === 'money' ? 'number' : f.type || 'text';
    input = <input type={type} value={String(value ?? '')} required={f.required} placeholder={f.placeholder} maxLength={f.maxLength} disabled={f.disabled}
      step={f.step ?? (f.type === 'money' ? '0.01' : f.type === 'number' ? 'any' : undefined)} style={style} onChange={e => onChange(e.target.value)} />;
  }
  return <label className={'f ' + (f.full ? 'full' : '')}>{f.label}{f.required ? ' *' : ''}{input}</label>;
}

export function Form({ fields, values, onSubmit, submitLabel, onCancel, busy, children, className = 'form', selfId }: {
  fields: Field[]; values?: object; onSubmit: (v: Values) => void; submitLabel?: string; onCancel?: () => void; busy?: boolean; children?: ReactNode; className?: string;
  /** id of the record being edited (lookups then skip it when looking for duplicates) */
  selfId?: number;
}) {
  const { t } = useTranslation();
  const [state, setState] = useState<Values>(() => initialValues(fields, values));
  const patch = useCallback((p: Values) => setState(s => ({ ...s, ...p })), []);
  return (
    <form className={className} noValidate onSubmit={e => {
      e.preventDefault();
      const missing = fields.some(f => f.required && f.type !== 'checkbox' && String(state[f.name] ?? '').trim() === '');
      if (missing) return toast(t('err.missing_fields'), true);
      onSubmit(readValues(fields, state));
    }}>
      {fields.map(f => (
        <Fragment key={f.name}>
          <FieldInput f={f} value={state[f.name]} onChange={v => setState(s => ({ ...s, [f.name]: v }))} />
          {f.lookup && f.lookup !== 'address' && <LookupHint kind={f.lookup} name={f.name} values={state} patch={patch} selfId={selfId} />}
        </Fragment>
      ))}
      {children}
      <div className={onCancel ? 'actions full' : 'full'}>
        {onCancel && <button type="button" className="btn" onClick={onCancel}>{t('common.cancel')}</button>}
        <button className="btn primary" disabled={busy}>{submitLabel ?? t('common.save')}</button>
      </div>
    </form>
  );
}

export function FormModal({ title, fields, values, onSubmit, onClose, submitLabel, wide, selfId }: {
  title: string; fields: Field[]; values?: object; onSubmit: (v: Values) => Promise<unknown>; onClose: () => void; submitLabel?: string; wide?: boolean; selfId?: number;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <Modal title={title} onClose={onClose} wide={wide}>
      <Form fields={fields} values={values} busy={busy} submitLabel={submitLabel} onCancel={onClose} selfId={selfId} onSubmit={async v => {
        setBusy(true);
        try { await onSubmit(v); onClose(); } catch (e) { toastError(e); } finally { setBusy(false); }
      }} />
    </Modal>
  );
}

/** Hook for "open a modal" state */
export function useModal<T = true>() {
  const [open, setOpen] = useState<T | null>(null);
  return { value: open, open: (v: T) => setOpen(v), close: () => setOpen(null) };
}

export const confirmDelete = () => window.confirm(i18n.t('common.confirm_delete'));

// ---------- Language switcher ----------
/** Changes the UI language now and, when signed in, saves it to the person's profile */
export function LanguageSwitch({ signedIn = false, className = '' }: { signedIn?: boolean; className?: string }) {
  const { i18n: inst, t } = useTranslation();
  const qc = useQueryClient();
  return (
    <select className={'lang-switch ' + className} aria-label={t('set.language')} value={inst.language}
      onChange={async e => {
        const lang = e.target.value;
        await setLanguage(lang);
        if (signedIn) {
          try { await api.put('/auth/me', { language: lang }); await qc.invalidateQueries({ queryKey: ['me'] }); } catch (err) { toastError(err); }
        }
      }}>
      {Object.entries(LANGUAGES).map(([code, name]) => <option key={code} value={code}>{name}</option>)}
    </select>
  );
}
